import {
  ethers,
  type Signer,
  type ContractTransactionResponse,
  type ContractTransactionReceipt,
  type LogDescription,
} from "ethers";
import { getChainConfig, parseUSDC, formatUSDC } from "@/config/contracts";
import { getTokenConfigs } from "@/config/tokens";
import type {
  ReputationDetail,
  KnownCurrency,
  OnchainContractKey,
} from "@/lib/types";
import type { TxContextSnapshot, TxContextTag, TxLogger } from "@/lib/txContext";
import { contractKeyFor, idFromTag } from "@/lib/txContext";
import { formatReputationScore } from "@/lib/reputationScale";
import { RpcChainMismatchError } from "@/lib/chainGuard";

import QuestManagerABI from "@/lib/abi/QuestManager.json";
import TaskManagerABI from "@/lib/abi/TaskManager.json";
import BountyBoardABI from "@/lib/abi/BountyBoard.json";
import EscrowVaultABI from "@/lib/abi/EscrowVault.json";
import UserRegistryABI from "@/lib/abi/UserRegistry.json";
import ReputationLedgerABI from "@/lib/abi/ReputationLedger.json";
import MockUSDCABI from "@/lib/abi/MockUSDC.json";

// Type for the Privy-sponsored send function injected from React hooks
export type SponsoredSendFn = (to: string, data: string, value?: bigint) => Promise<string>;

// Optional callback fired after every on-chain send (debug instrumentation)
export type OnSendFn = (info: { to: string; method: string; txHash: string }) => void;

// Optional provider used only for waitForTransaction. Prefer a WebSocketProvider
// here so receipt resolution is push-based instead of HTTP-polled — this knocks
// ~1–2s off every sponsored send when the env exposes a wss:// endpoint.
export type ReceiptProvider = ethers.AbstractProvider;

// Cap on the WebSocket receipt wait before falling back to HTTP polling. A
// socket connected to a different chain than the tx was sent on would
// otherwise wait forever (see the call site).
const WS_RECEIPT_TIMEOUT_MS = Number(
  import.meta.env.VITE_WS_RECEIPT_TIMEOUT_MS ?? 20_000,
);

// Privy's embedded-wallet RPC endpoint rate-limits per wallet. We gate the
// START of each sponsored send so consecutive calls are at least this many ms
// apart. If a tx already took longer than the gap (RPC + receipt wait), no
// extra wait is added. Tunable via VITE_PRIVY_TX_MIN_GAP_MS.
const PRIVY_TX_MIN_GAP_MS = Math.max(
  0,
  Number(import.meta.env?.VITE_PRIVY_TX_MIN_GAP_MS ?? 2500),
);

async function call<T>(contract: ethers.Contract, method: string, ...args: unknown[]): Promise<T> {
  return contract.getFunction(method)(...args) as Promise<T>;
}

function cryptoRandomUUID(): string {
  // crypto.randomUUID is available in modern browsers and Node 14.17+,
  // but fall back to a tiny v4-ish generator for broad safety.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ─── PolygonClient ──────────────────────────────────────

export class PolygonClient {
  readonly signer: Signer;
  private readonly tokenContracts: ReadonlyMap<KnownCurrency, ethers.Contract>;
  readonly questManager: ethers.Contract;
  readonly taskManager: ethers.Contract;
  readonly bountyBoard: ethers.Contract;
  readonly escrowVault: ethers.Contract;
  readonly userRegistry: ethers.Contract;
  readonly reputationLedger: ethers.Contract;
  private readonly sponsoredSend?: SponsoredSendFn;
  private readonly txContext?: TxContextSnapshot;
  private readonly txLogger?: TxLogger;
  private readonly contractAddresses: Record<OnchainContractKey, string>;
  private currentTag: TxContextTag | null = null;
  private readonly onSend?: OnSendFn;
  private readonly receiptProvider?: ReceiptProvider;
  // Timestamp of the last sponsoredSend dispatch. Used by the per-instance
  // throttle to keep Privy's embedded-wallet RPC under its rate limit.
  private lastSponsoredSendStart = 0;
  // Single-slot mutex so concurrent callers serialize through the throttle
  // even when fired from independent code paths (e.g. parallelized loops).
  private sponsoredSendChain: Promise<unknown> = Promise.resolve();

  constructor(
    signer: Signer,
    sponsoredSend?: SponsoredSendFn,
    txContext?: TxContextSnapshot,
    txLogger?: TxLogger,
    onSend?: OnSendFn,
    receiptProvider?: ReceiptProvider,
  ) {
    this.signer = signer;
    this.sponsoredSend = sponsoredSend;
    this.txContext = txContext;
    this.txLogger = txLogger;
    this.onSend = onSend;
    this.receiptProvider = receiptProvider;
    const cfg = getChainConfig();
    const c = cfg.contracts;

    // Data-driven token contracts — share the parsed ABI interface
    const erc20Interface = new ethers.Interface(MockUSDCABI);
    const tokenConfigs = getTokenConfigs();
    this.tokenContracts = new Map(
      [...tokenConfigs.entries()].map(([currency, config]) => [
        currency,
        new ethers.Contract(config.address, erc20Interface, signer),
      ]),
    );

    this.questManager = new ethers.Contract(c.questManager, QuestManagerABI, signer);
    this.taskManager = new ethers.Contract(c.taskManager, TaskManagerABI, signer);
    this.bountyBoard = new ethers.Contract(c.bountyBoard, BountyBoardABI, signer);
    this.escrowVault = new ethers.Contract(c.escrowVault, EscrowVaultABI, signer);
    this.userRegistry = new ethers.Contract(c.userRegistry, UserRegistryABI, signer);
    this.reputationLedger = new ethers.Contract(c.reputationLedger, ReputationLedgerABI, signer);

    this.contractAddresses = {
      questManager: c.questManager,
      taskManager: c.taskManager,
      bountyBoard: c.bountyBoard,
      escrowVault: c.escrowVault,
      userRegistry: c.userRegistry,
      reputationLedger: c.reputationLedger,
      disputeResolver: c.disputeResolver,
      mockUSDC: c.mockUSDC,
      token: "", // not a single address; matched via tokenContracts loop at send time
    };
  }

  /**
   * Tag every tx issued inside the callback with the given flow + entity refs.
   * Nests correctly — an outer tag is restored after the callback resolves.
   * One-line call-site usage:
   *   client.withContext({ flow: "userSubmittedWork", taskId }, () => client.submitWork(...))
   */
  async withContext<T>(tag: TxContextTag, fn: () => Promise<T>): Promise<T> {
    const prev = this.currentTag;
    this.currentTag = { ...(prev ?? {}), ...tag };
    try {
      return await fn();
    } finally {
      this.currentTag = prev;
    }
  }

  // ─── Private send ───────────────────────────────────────

  private async send(
    contract: ethers.Contract,
    method: string,
    ...args: unknown[]
  ): Promise<ContractTransactionReceipt> {
    const attemptId = cryptoRandomUUID();
    this.logSendStart(attemptId, contract, method);

    try {
      const receipt = await this.executeSend(contract, method, args);
      this.logSendSuccess(attemptId, receipt);
      return receipt;
    } catch (err) {
      this.logSendFailure(attemptId, err);
      throw err;
    }
  }

  /** Wait until at least PRIVY_TX_MIN_GAP_MS has elapsed since the last
   * sponsored dispatch. Updates the timestamp once we're cleared to send. */
  private async throttleSponsoredSend(): Promise<void> {
    if (PRIVY_TX_MIN_GAP_MS <= 0) return;
    const elapsed = Date.now() - this.lastSponsoredSendStart;
    const wait = PRIVY_TX_MIN_GAP_MS - elapsed;
    if (wait > 0) {
      await new Promise((r) => setTimeout(r, wait));
    }
    this.lastSponsoredSendStart = Date.now();
  }

  /** Serialize callers through `sponsoredSendChain` so concurrent fan-outs
   * still respect the throttle. Returns the inner result unchanged. */
  private withSponsoredSendLock<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.sponsoredSendChain.then(fn, fn);
    // Swallow rejection on the chain link itself so one failure doesn't
    // break the queue for subsequent callers.
    this.sponsoredSendChain = next.catch(() => undefined);
    return next;
  }

  private async executeSend(
    contract: ethers.Contract,
    method: string,
    args: unknown[],
  ): Promise<ContractTransactionReceipt> {
    if (this.sponsoredSend) {
      const fragment = contract.interface.getFunction(method);
      if (!fragment) throw new Error(`Method ${method} not found in ABI`);
      const data = contract.interface.encodeFunctionData(fragment, args);
      const to = await contract.getAddress();

      // Retry with exponential backoff for rate limit (429) errors.
      // 6 attempts × capped backoff covers Privy's per-wallet RPC limit
      // even when the throttle hasn't fully spaced things out (e.g. on
      // the very first tx after a long idle).
      const maxAttempts = 6;
      let lastError: Error | null = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const hash = await this.withSponsoredSendLock(async () => {
            await this.throttleSponsoredSend();
            return this.sponsoredSend!(to, data);
          });
          // Prefer the WebSocket receipt provider when available (push-based,
          // ~500ms vs ~2s for HTTP polling); fall back to the signer's provider
          // both when WS is absent and when the WS layer errors mid-flight.
          const httpProvider = this.signer.provider;
          if (!this.receiptProvider && !httpProvider) {
            throw new Error("No provider attached to signer");
          }
          let receipt: ethers.TransactionReceipt | null = null;
          if (this.receiptProvider) {
            try {
              // Bounded on purpose. ethers' waitForTransaction with no timeout
              // recurses on "block" with no timer, so a socket pointed at the
              // wrong network never resolves AND never throws -- the catch
              // below would not fire and the HTTP fallback would be
              // unreachable, hanging after the funds already moved.
              receipt = await this.receiptProvider.waitForTransaction(
                hash,
                1,
                WS_RECEIPT_TIMEOUT_MS,
              );
              if (!receipt) {
                console.warn(
                  `[PolygonClient] WS receipt timed out for ${method} after ${WS_RECEIPT_TIMEOUT_MS}ms, falling back to HTTP`,
                );
              }
            } catch (wsErr) {
              console.warn(
                `[PolygonClient] WS receipt failed for ${method}, falling back to HTTP:`,
                wsErr,
              );
            }
          }
          if (!receipt && httpProvider) {
            receipt = await httpProvider.waitForTransaction(hash);
          }
          if (!receipt) throw new Error(`No receipt for ${method}`);
          try { this.onSend?.({ to, method, txHash: hash }); } catch { /* debug must not break tx */ }
          return receipt as unknown as ContractTransactionReceipt;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          const isRateLimit =
            lastError.message.includes("Too many requests") ||
            lastError.message.includes("429") ||
            lastError.message.includes("too_many_requests");
          if (!isRateLimit || attempt === maxAttempts - 1) throw lastError;
          // Exponential backoff capped at 12s so a sustained 429 burst still
          // gets retried within a reasonable window. Small jitter avoids
          // synchronized retries across parallel callers.
          const delay = Math.min(2 ** attempt * 2000, 12000) + Math.random() * 1000;
          console.warn(
            `[PolygonClient] Rate limited on ${method}, retry ${attempt + 1}/${maxAttempts - 1} in ${Math.round(delay)}ms`,
          );
          await new Promise((r) => setTimeout(r, delay));
          // Penalize the throttle so the next dispatch waits an extra slot.
          this.lastSponsoredSendStart = Date.now();
        }
      }
      throw lastError ?? new Error(`Failed ${method} after retries`);
    }

    // Fallback: direct ethers path (mock mode, no paymaster)
    const tx: ContractTransactionResponse = await contract.getFunction(method)(...args);
    const receipt = await tx.wait();
    if (!receipt) throw new Error(`No receipt for ${method}`);
    try {
      const to = await contract.getAddress();
      this.onSend?.({ to, method, txHash: receipt.hash });
    } catch { /* debug must not break tx */ }
    return receipt;
  }

  // ─── tx log plumbing (fire-and-forget) ─────────────────

  private logSendStart(
    attemptId: string,
    contract: ethers.Contract,
    method: string,
  ): void {
    if (!this.txLogger) {
      console.warn(
        `[PolygonClient] tx log skipped for ${method}: no logger attached ` +
          `(likely mock-auth mode)`,
      );
      return;
    }
    if (!this.txContext) {
      console.warn(
        `[PolygonClient] tx log skipped for ${method}: no context snapshot ` +
          `(usePolygonClient built the client before Privy user.id resolved)`,
      );
      return;
    }
    const ctx = this.txContext;
    const tag = this.currentTag;

    // Resolve contract address synchronously via a target lookup — ethers
    // contracts carry their `target` (the address) already; avoids an
    // async await in a fire-and-forget path.
    const address = typeof contract.target === "string" ? contract.target : "";
    let contractKey = contractKeyFor(address, this.contractAddresses);
    if (!contractKey) {
      // Token contracts resolve here (they share the ERC20 interface).
      for (const c of this.tokenContracts.values()) {
        if (typeof c.target === "string" && c.target.toLowerCase() === address.toLowerCase()) {
          contractKey = "token";
          break;
        }
      }
    }
    if (!contractKey) {
      console.warn(
        `[PolygonClient] tx log skipped for ${method}: unknown contract at ${address}`,
      );
      return;
    }

    console.info(
      `[PolygonClient] logging ${contractKey}.${method} (attempt ${attemptId}, flow=${tag?.flow ?? "-"})`,
    );

    this.txLogger
      .log({
        attemptId,
        chainId: ctx.chainId,
        privyDid: ctx.privyDid,
        uiResolvedSigner: ctx.uiResolvedSigner,
        storedWallet: ctx.storedWallet,
        registryWallet: null,
        linkedWallets: ctx.linkedWallets,
        contractKey,
        method,
        initiatorFlow: tag?.flow ?? null,
        relatedQuestId: idFromTag(tag?.questId),
        relatedTaskId: idFromTag(tag?.taskId),
        sponsored: ctx.sponsored,
      })
      .catch((err) => {
        console.error(
          `[PolygonClient] tx log insert FAILED for ${method}:`,
          err,
          { attemptId, privyDid: ctx.privyDid, uiResolvedSigner: ctx.uiResolvedSigner },
        );
      });

    // Stash the attempt id so the success/failure updaters can find it
    // without threading through the returns.
    this.pendingAttempts.set(attemptId, { method });
  }

  private readonly pendingAttempts = new Map<string, { method: string }>();

  private logSendSuccess(attemptId: string, receipt: ContractTransactionReceipt): void {
    if (!this.txLogger) return;
    if (!this.pendingAttempts.delete(attemptId)) return;

    const gasPrice =
      "gasPrice" in receipt && receipt.gasPrice != null
        ? (receipt.gasPrice as bigint).toString()
        : null;

    this.txLogger
      .update(attemptId, {
        txHash: receipt.hash,
        receiptFrom: receipt.from ?? null,
        status: receipt.status === 1 ? "success" : "reverted",
        blockNumber: receipt.blockNumber ?? null,
        gasUsed: receipt.gasUsed != null ? Number(receipt.gasUsed) : null,
        effectiveGasPrice: gasPrice,
        confirmedAt: new Date().toISOString(),
      })
      .catch((err) => {
        console.error(`[PolygonClient] tx log success update FAILED:`, err, { attemptId });
      });
  }

  private logSendFailure(attemptId: string, err: unknown): void {
    if (!this.txLogger) return;
    if (!this.pendingAttempts.delete(attemptId)) return;

    const message = err instanceof Error ? err.message : String(err);
    this.txLogger
      .update(attemptId, {
        status: "dropped",
        errorReason: message.slice(0, 2000),
      })
      .catch((e) => {
        console.error(`[PolygonClient] tx log failure update FAILED:`, e, { attemptId });
      });
  }

  // ─── Helpers ────────────────────────────────────────────

  hashMetadata(json: Record<string, unknown>): string {
    return ethers.keccak256(ethers.toUtf8Bytes(JSON.stringify(json)));
  }

  hashString(s: string): string {
    return ethers.keccak256(ethers.toUtf8Bytes(s));
  }

  hashSkills(skills: string[]): string {
    return ethers.keccak256(ethers.toUtf8Bytes(JSON.stringify([...skills].sort())));
  }

  async getAddress(): Promise<string> {
    return this.signer.getAddress();
  }

  // ─── Token operations (multi-token) ────────────────────

  getTokenContract(currency: KnownCurrency): ethers.Contract {
    const contract = this.tokenContracts.get(currency);
    if (!contract) throw new Error(`No contract configured for ${currency}`);
    return contract;
  }

  async approveToken(currency: KnownCurrency, spender: string, amount: number): Promise<ContractTransactionReceipt | null> {
    const contract = this.getTokenContract(currency);
    const config = getTokenConfigs().get(currency)!;
    const owner = await this.getAddress();
    const parsedAmount = parseUSDC(amount);

    // Check existing allowance — skip if already sufficient. Returning null
    // (rather than an empty cast-as-receipt object) keeps the type honest
    // for any caller that reads receipt fields.
    const currentAllowance: bigint = await call<bigint>(contract, "allowance", owner, spender);
    if (currentAllowance >= parsedAmount) {
      return null;
    }

    // USDT: reset to zero first if non-zero (non-standard approve behavior)
    if (config.requiresApproveReset && currentAllowance > 0n) {
      await this.send(contract, "approve", spender, 0n);
    }

    // Approve MaxUint256 so subsequent quests by the same creator skip this step
    // entirely (the allowance check above short-circuits). Spender is always a
    // weXare protocol contract — risk surface is the same as a per-quest approve.
    return this.send(contract, "approve", spender, ethers.MaxUint256);
  }

  async getTokenBalance(currency: KnownCurrency, addr: string): Promise<number> {
    const contract = this.getTokenContract(currency);
    const raw = await call<bigint>(contract, "balanceOf", addr);
    return formatUSDC(raw);
  }

  async mintTestToken(currency: KnownCurrency, to: string, amount: number): Promise<ContractTransactionReceipt> {
    const contract = this.getTokenContract(currency);
    return this.send(contract, "mint", to, parseUSDC(amount));
  }

  // ─── User Registry ─────────────────────────────────────

  async registerUser(
    handle: string,
    profileJson: Record<string, unknown>,
    skills: string[],
    rolePref: "creator" | "hunter" | "both",
  ): Promise<ContractTransactionReceipt> {
    const roleMap = { creator: 0, hunter: 1, both: 2 } as const;
    return this.send(
      this.userRegistry,
      "registerUser",
      this.hashString(handle),
      this.hashMetadata(profileJson),
      this.hashSkills(skills),
      roleMap[rolePref],
    );
  }

  async isRegistered(addr: string): Promise<boolean> {
    return call<boolean>(this.userRegistry, "isRegistered", addr);
  }

  async updateProfile(profileJson: Record<string, unknown>): Promise<ContractTransactionReceipt> {
    return this.send(this.userRegistry, "updateProfile", this.hashMetadata(profileJson));
  }

  async updateSkills(skills: string[]): Promise<ContractTransactionReceipt> {
    return this.send(this.userRegistry, "updateSkills", this.hashSkills(skills));
  }

  // ─── Quest Manager ─────────────────────────────────────

  async createQuest(
    metadataJson: Record<string, unknown>,
    category: string,
    budgetUSD: number,
    deadline: number,
    tokenAddress: string,
  ): Promise<{ questId: bigint; receipt: ContractTransactionReceipt }> {
    const receipt = await this.send(
      this.questManager,
      "createQuest",
      this.hashMetadata(metadataJson),
      this.hashString(category),
      parseUSDC(budgetUSD),
      BigInt(deadline),
      tokenAddress,
    );
    const questId = this.parseEventId(receipt, this.questManager, "QuestCreated", "questId");
    return { questId, receipt };
  }

  async fundQuest(questId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.questManager, "fundQuest", questId);
  }

  async publishQuest(questId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.questManager, "publishQuest", questId);
  }

  async openAllTasks(questId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.taskManager, "openAllTasks", questId);
  }

  async closeQuest(questId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.questManager, "closeQuest", questId);
  }

  async cancelQuest(questId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.questManager, "cancelQuest", questId);
  }

  // ─── Task Manager ──────────────────────────────────────

  async createTask(
    questId: bigint,
    metadataJson: Record<string, unknown>,
    taskType: "human" | "ai",
    budgetUSD: number,
    effortHours: number,
    deadline: number,
    skills: string[],
    order: number,
  ): Promise<{ taskId: bigint; receipt: ContractTransactionReceipt }> {
    const taskTypeUint = taskType === "human" ? 0 : 1;
    const effortSec = BigInt(Math.round(effortHours * 3600));
    const receipt = await this.send(
      this.taskManager,
      "createTask",
      questId,
      this.hashMetadata(metadataJson),
      taskTypeUint,
      parseUSDC(budgetUSD),
      effortSec,
      BigInt(deadline),
      this.hashSkills(skills),
      order,
    );
    const taskId = this.parseEventId(receipt, this.taskManager, "TaskCreated", "taskId");
    return { taskId, receipt };
  }

  async startTask(taskId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.taskManager, "startTask", taskId);
  }

  async submitWork(
    taskId: bigint,
    submissionJson: Record<string, unknown>,
  ): Promise<ContractTransactionReceipt> {
    return this.send(this.taskManager, "submitWork", taskId, this.hashMetadata(submissionJson));
  }

  async approveSubmission(taskId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.taskManager, "approveSubmission", taskId);
  }

  async requestChanges(
    taskId: bigint,
    feedbackJson: Record<string, unknown>,
  ): Promise<ContractTransactionReceipt> {
    return this.send(this.taskManager, "requestChanges", taskId, this.hashMetadata(feedbackJson));
  }

  // ─── Direct Task Assignment ────────────────────────────

  /** Assign a task directly via TaskManager (creator or OPERATOR_ROLE). Bypasses BountyBoard. */
  async assignTask(taskId: bigint, assigneeAddr: string): Promise<ContractTransactionReceipt> {
    return this.send(this.taskManager, "assignTask", taskId, assigneeAddr);
  }

  // ─── Bounty Board ──────────────────────────────────────

  async expressInterest(taskId: bigint): Promise<ContractTransactionReceipt> {
    return this.send(this.bountyBoard, "expressInterest", taskId);
  }

  async acceptApplicant(
    taskId: bigint,
    applicantAddr: string,
  ): Promise<ContractTransactionReceipt> {
    return this.send(this.bountyBoard, "acceptApplicant", taskId, applicantAddr);
  }

  // ─── Escrow Vault (reads) ──────────────────────────────

  async getEscrowBalance(questId: bigint): Promise<number> {
    const raw = await call<bigint>(this.escrowVault, "getAvailableBalance", questId);
    return formatUSDC(raw);
  }

  async getEscrow(questId: bigint): Promise<{
    totalAmount: number;
    releasedAmount: number;
    refundedAmount: number;
    status: number;
    token: string;
  }> {
    const e = await call<{
      totalAmount: bigint;
      releasedAmount: bigint;
      refundedAmount: bigint;
      status: bigint;
      token: string;
    }>(this.escrowVault, "getEscrow", questId);
    return {
      totalAmount: formatUSDC(e.totalAmount),
      releasedAmount: formatUSDC(e.releasedAmount),
      refundedAmount: formatUSDC(e.refundedAmount),
      status: Number(e.status),
      token: e.token,
    };
  }

  // ─── Reputation Ledger (reads) ─────────────────────────

  async getReputation(addr: string): Promise<ReputationDetail> {
    const r = await call<{
      overall: bigint;
      completionRate: bigint;
      qualityScore: bigint;
      speedScore: bigint;
      reliability: bigint;
      tasksCompleted: bigint;
      tasksDisputed: bigint;
      questsCreated: bigint;
      questsCompleted: bigint;
      lastUpdated: bigint;
    }>(this.reputationLedger, "getReputation", addr).catch((err) =>
      this.explainEmptyRead(err),
    );
    // The ledger stores basis points (0–10000); the UI reads these as literal
    // percentages. Convert here — the contracts are frozen, so this boundary is
    // the only place the scale can be corrected.
    return {
      overall: formatReputationScore(r.overall),
      completionRate: formatReputationScore(r.completionRate),
      qualityScore: formatReputationScore(r.qualityScore),
      speedScore: formatReputationScore(r.speedScore),
      reliability: formatReputationScore(r.reliability),
      // Counters are plain tallies, not basis points — no conversion.
      tasksCompleted: Number(r.tasksCompleted),
      tasksDisputed: Number(r.tasksDisputed),
      questsCreated: Number(r.questsCreated),
      questsCompleted: Number(r.questsCompleted),
      lastUpdated: Number(r.lastUpdated),
    };
  }

  /**
   * Rethrow a failed read, naming the cause when it is a misrouted RPC.
   *
   * A view call to an address with no code returns empty `0x`, which ethers
   * reports as BAD_DATA. The usual reason in this app is the RPC answering as
   * the other chain, where the per-chain contract address is empty -- so check
   * that before letting a generic decode error through.
   */
  private async explainEmptyRead(err: unknown): Promise<never> {
    if (ethers.isError(err, "BAD_DATA") && err.value === "0x") {
      const expected = getChainConfig().chainId;
      const network = await this.signer.provider?.getNetwork().catch(() => null);
      if (network && Number(network.chainId) !== expected) {
        throw new RpcChainMismatchError(expected, Number(network.chainId));
      }
    }
    throw err;
  }

  // ─── Event parsing ─────────────────────────────────────

  private parseEventId(
    receipt: ContractTransactionReceipt,
    contract: ethers.Contract,
    eventName: string,
    idField: string,
  ): bigint {
    for (const log of receipt.logs) {
      try {
        const parsed: LogDescription | null = contract.interface.parseLog({
          topics: [...log.topics],
          data: log.data,
        });
        if (parsed?.name === eventName) {
          return BigInt(parsed.args[idField]);
        }
      } catch {
        // Not from this contract, skip
      }
    }
    throw new Error(`Event ${eventName} not found in transaction receipt`);
  }
}

// ─── Error decoding ─────────────────────────────────────

const ERROR_MESSAGES: Record<string, string> = {
  // Protocol custom errors
  AlreadyRegistered: "This wallet is already registered on-chain.",
  HandleTaken: "This handle is already taken.",
  NotCreator: "Only the quest creator can perform this action.",
  NotAssignee: "Only the assigned user can perform this action.",
  InsufficientEscrow: "Insufficient escrow balance for this operation.",
  QuestNotFound: "Quest not found on-chain.",
  TaskNotFound: "Task not found on-chain.",
  InvalidStatus: "Invalid status transition.",
  AlreadyExpressed: "You have already expressed interest in this task.",
  NotRegistered: "You must register on-chain before performing this action.",
  ProtocolPaused: "The protocol is currently paused.",
  DeadlinePassed: "The deadline for this action has passed.",
  BudgetExceeded: "Task budgets exceed the quest budget.",
  AlreadyAssigned: "This task is already assigned.",
  NoApplicants: "No applicants for this task.",
  EscrowExists: "This quest has already been funded.",
  ZeroBudget: "Budget must be greater than zero.",
  ZeroAmount: "Amount must be greater than zero.",
  // OZ ERC-20 / AccessControl errors
  TokenNotAllowed: "This token is not supported by the protocol.",
  FeeOnTransferNotSupported: "Fee-on-transfer tokens are not supported.",
  ERC20InsufficientBalance: "Insufficient token balance in your wallet.",
  ERC20InsufficientAllowance: "Token spending not authorized — please retry.",
  ERC20InvalidReceiver: "Invalid token transfer destination.",
  AccessControlUnauthorizedAccount: "Protocol configuration error — contract role missing.",
};

const ALL_ABIS = [
  QuestManagerABI,
  TaskManagerABI,
  BountyBoardABI,
  EscrowVaultABI,
  UserRegistryABI,
  ReputationLedgerABI,
  MockUSDCABI,
];

export function getContractErrorName(error: unknown): string | null {
  const rawErrData =
    (error as { data?: unknown })?.data ??
    (error as { error?: { data?: unknown } })?.error?.data;

  // ethers / Privy paymaster sometimes surface `data` as an object (e.g. the
  // UserOperationRevertReason payload). parseError requires a 0x-prefixed
  // hex string; pass anything else and it throws synchronously.
  if (typeof rawErrData !== "string" || !rawErrData || rawErrData === "0x") {
    return null;
  }

  for (const abi of ALL_ABIS) {
    try {
      const iface = new ethers.Interface(abi);
      const decoded = iface.parseError(rawErrData);
      if (decoded) return decoded.name;
    } catch {
      // Not from this ABI
    }
  }
  return null;
}

export function decodeContractError(error: unknown): string {
  const name = getContractErrorName(error);
  // Map known errors to their user-friendly copy. Unknown error names are
  // intentionally collapsed into a generic message so internal contract
  // guard names aren't surfaced to UI toasts.
  if (name && ERROR_MESSAGES[name]) return ERROR_MESSAGES[name];

  const msg = (error as Error)?.message ?? String(error);
  if (msg.includes("user rejected")) return "Transaction rejected by user.";
  if (msg.includes("insufficient funds")) return "Insufficient funds for gas.";
  return "Transaction failed. Please try again.";
}
